import { initContract } from '@ts-rest/core';
import { createApi } from './ts-rest';
import type { CopilotMessage, ServerSideCopilotToolPayload } from '@customerio/hydra-copilot';
import type { RawAccount, RawUser, SearchResult, Workspace } from '../auth/types';
import type { AIContext, AIContextOverrides } from '@/pages/journeys/settings/ai-business-profile/types';
import { crossAppCache } from '@/utils/cross-app-cache';
import type { BackfillType } from '@/features/tasks/utils/task-types';

/**
 * Common/Shared Types
 */

export interface ServicesApiPaginationMeta {
  page: number;
  size: number;
  total: number;
}

export interface ServicesApiErrorSource {
  pointer: string;
}

export interface ServicesApiErrorDetail {
  code?: string;
  detail: string;
  source?: ServicesApiErrorSource;
  status: string;
  meta?: Record<string, unknown>;
}

export interface ServicesApiErrorResponse {
  errors: ServicesApiErrorDetail[];
}

/**
 * Account API Types
 */

export interface ServicesApiAccountResponse {
  account: RawAccount;
  environments: Workspace[];
  user: RawUser;
  session_expires_at: number;
}

export interface ServicesApiGetAccountByIdPathParams {
  accountId: number;
}

export interface ServicesApiGetAccountByEnvironmentIdPathParams {
  environmentId: number;
}

/**
 * Notifications API Types
 */

export interface ServicesApiNotification {
  id: number;
  title: string;
  body: string;
  link_text: string;
  link_url: string;
  activated: number;
  created: number;
  updated: number;
  viewed: number;
}

export interface ServicesApiGetNotificationsResponse {
  notifications: ServicesApiNotification[];
}

export interface ServicesApiGetNotificationsPathParams {
  accountId: number;
}

export interface ServicesApiMarkNotificationsAsViewedPathParams {
  accountId: number;
}

export interface ServicesApiMarkNotificationsAsViewedBody {
  ids: number[];
}

/**
 * Copilot API Types
 */

export interface ServicesApiCreateCopilotSessionPathParams {
  accountId: number;
}

export interface ServicesApiCreateCopilotSessionResponse {
  session_id: string;
  tools: ServerSideCopilotToolPayload[];
}

export interface ServicesApiDeleteCopilotSessionPathParams {
  accountId: number;
  sessionId: number;
}

export interface ServicesApiGetCopilotSessionPathParams {
  accountId: number;
  sessionId: number;
}

export interface ServicesApiGetCopilotSessionResponse {
  name: string;
  messages: CopilotMessage[];
  tools: ServerSideCopilotToolPayload[];
}

export interface ServicesApiGetCopilotFileUploadSignaturePathParams {
  accountId: number;
  sessionId: string;
}

export interface ServicesApiGetCopilotFileUploadSignatureBody {
  size: string;
  mime_type: string;
}

export interface ServicesApiGetCopilotFileUploadSignatureResponse {
  filename: string;
  signature: {
    fields: {
      'content-type': string;
      key: string;
      policy: string;
      success_action_status: string;
      'x-goog-algorithm': string;
      'x-goog-credential': string;
      'x-goog-date': string;
      'x-goog-meta-account-id': string;
      'x-goog-meta-session-id': string;
      'x-goog-signature': string;
    };
    url: string;
  };
}

export interface ServicesApiGetCopilotFileDownloadUrlPathParams {
  accountId: number;
  sessionId: string;
  filename: string;
}

export interface ServicesApiGetCopilotFileDownloadUrlResponse {
  url: string;
}

export interface ServicesApiUpdateCopilotSessionPathParams {
  accountId: number;
  sessionId: number;
}

export interface ServicesApiUpdateCopilotSessionBody {
  name: string;
}

export interface ServicesApiGetCopilotSessionsPathParams {
  accountId: number;
}

export interface ServicesApiGetCopilotSessionsQueryParams {
  page?: number;
}

export interface ServicesApiCopilotSessionSummary {
  id: number;
  name: string;
  created_at: number;
  updated_at: number;
}

export interface ServicesApiGetCopilotSessionsResponse {
  meta: {
    pagination: ServicesApiPaginationMeta;
  };
  sessions: ServicesApiCopilotSessionSummary[];
}

export interface ServicesApiRecordCopilotFeedbackPathParams {
  accountId: number;
  sessionId: number;
  logId: string;
}

export interface ServicesApiRecordCopilotFeedbackBody {
  thumbs_up?: boolean;
  thumbs_down?: boolean;
  feedback_reason?: string;
}

export interface ServicesApiRecordCopilotFeedbackResponse {
  success: boolean;
}

export interface ServicesApiGetAIContextOverridesPathParams {
  workspaceId: number;
}

export interface ServicesApiGetAIContextOverridesResponse {
  ai_context?: AIContext;
  overrides?: AIContextOverrides;
}

export interface ServicesApiUpdateAIContextOverridesPathParams {
  workspaceId: number;
}

export interface ServicesApiUpdateAIContextOverridesBody {
  overrides: AIContextOverrides;
}

export interface ServicesApiUpdateAIContextOverridesResponse {
  ai_context?: AIContext;
  overrides?: AIContextOverrides;
}

/**
 * Feedback API Types
 */

export interface ServicesApiSubmitFeedbackPathParams {
  accountId: number;
}

export interface ServicesApiSubmitFeedbackBody {
  replyTo: string;
  accountId: number;
  accountEmail?: string;
  userFirstName: string;
  userLastName: string;
  browser: string;
  url: string;
  windowDimensions: string;
  criticism: string;
  praise: string;
}

/**
 * OAuth2 MCP API Types
 */

export interface ServicesApiOAuthMcpResponse {
  location: string;
}

/**
 * Zendesk Chat API Types
 */

export interface ServicesApiGetZendeskChatAccessTokenPathParams {
  accountId: number;
  userId: number;
}

export interface ServicesApiGetZendeskChatAccessTokenResponse {
  token: string;
  is_handover_allowed: boolean;
  token_lifetime_minutes: number;
}

export interface ServicesApiRefreshZendeskChatTokenPathParams {
  accountId: number;
  userId: number;
}

export interface ServicesApiRefreshZendeskChatTokenResponse {
  token: string;
}

/**
 * Tracking API Types
 */

export interface ServicesApiTrackEventPathParams {
  workspaceId: number;
}

export interface ServicesApiTrackEventBody {
  properties: Record<string, unknown>;
  event: string;
}

/**
 * Audit Logs API Types
 */

export interface ServicesApiGetAllUsersPathParams {
  account_id: number;
}

export interface ServicesApiAuditLogUser {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  created_at: number;
  updated_at: number;
  deleted: boolean;
}

export interface ServicesApiAuditLogUserSummary {
  user_id: number;
  email: string;
  deleted: boolean;
}

export interface ServicesApiGetAllUsersResponse {
  users: ServicesApiAuditLogUser[];
}

export interface ServicesApiAuditLogsQueryParams {
  version?: number;
  limit?: number;
  sort_order?: 'asc' | 'desc';
  cursor?: string;
  user_id?: number;
  ip_addr?: string;
  object_type?: string;
  object_id?: string;
  action?: string;
  attribute_name?: string;
  start_time?: string;
  end_time?: string;
}

export interface ServicesApiAuditLog {
  id: number;
  timestamp: number;
  account_id: number;
  user_id: number;
  ip_addr: string;
  object_type: string;
  object_id: string;
  version: number;
  action: string;
  data: unknown;
  meta?: unknown;
  changed_from?: Record<string, unknown> | null;
  changed_to?: Record<string, unknown> | null;
}

export interface ServicesApiAuditLogsPagination {
  limit: number;
  sort_order: string;
  next_cursor: number;
}

export interface ServicesApiGetAccountAuditLogsPathParams {
  account_id: number;
}

export interface ServicesApiGetAuditLogsResponse {
  data: ServicesApiAuditLog[];
  pagination: ServicesApiAuditLogsPagination;
  users: ServicesApiAuditLogUserSummary[];
}

export interface ServicesApiGetWorkspaceAuditLogsPathParams {
  environment_id: number;
}

export interface ServicesApiGetAuditLogByIdQueryParams {
  version?: number;
}

export interface ServicesApiGetAccountAuditLogByIdPathParams {
  account_id: number;
  id: number;
}

export interface ServicesApiGetAuditLogByIdResponse {
  data: ServicesApiAuditLog;
  user?: {
    user_id: number;
    email: string;
    deleted?: boolean;
  };
}

export interface ServicesApiGetWorkspaceAuditLogByIdPathParams {
  environment_id: number;
  id: number;
}

/**
 * Audit Logs Export API Types
 */

export interface ServicesApiExportAuditLogsBody {
  start_time: string; // Required, RFC3339 format
  end_time: string; // Required, RFC3339 format
  object_type?: string; // Optional filter
  environment_id?: number; // Required for account-level exports (workspace to create export in)
}

export interface ServicesApiExportAuditLogsResponse {
  export_id: string;
}

export interface ServicesApiExportAccountAuditLogsPathParams {
  account_id: number;
}

export interface ServicesApiExportWorkspaceAuditLogsPathParams {
  environment_id: number;
}

/**
 * Search API Types
 */

export interface ServicesApiSearchPathParams {
  workspaceId: number;
}

export interface ServicesApiSearchQueryParams {
  q: string;
  limit?: number;
  test?: number;
}

export interface ServicesApiSearchResponse {
  results: SearchResult[];
  query: string;
  limit: number;
}

/**
 * Campaigns API Types
 */

export interface ServicesApiGetCampaignsPathParams {
  environment_id: number;
}

export interface ServicesApiGetCampaignsQueryParams {
  page?: number;
  searchString?: string;
  archived?: boolean;
}

export interface ServicesApiCampaign {
  id: number;
  name: string;
  type: string;
  state: string;
  archived: boolean;
}

export interface ServicesApiGetCampaignsResponse {
  campaigns: ServicesApiCampaign[];
  meta?: {
    pagination?: ServicesApiPaginationMeta;
  };
}

/**
 * Segments API Types
 */

export interface ServicesApiGetSegmentsPathParams {
  environment_id: number;
}

export interface ServicesApiSegment {
  id: number;
  name: string;
  type: string;
  state: string;
}

export interface ServicesApiGetSegmentsResponse {
  segments: ServicesApiSegment[];
}

/**
 * Newsletters API Types
 */

export interface ServicesApiGetNewslettersPathParams {
  environment_id: number;
}

export interface ServicesApiGetNewslettersQueryParams {
  page?: number;
  searchString?: string;
  archived?: boolean;
}

export interface ServicesApiNewsletter {
  id: number;
  name: string;
  state: string;
}

export interface ServicesApiGetNewslettersResponse {
  newsletters: ServicesApiNewsletter[];
  meta?: {
    pagination?: ServicesApiPaginationMeta;
  };
}

/**
 * Transactional Messages API Types
 */

export interface ServicesApiGetTransactionalMessagesPathParams {
  environment_id: number;
}

export interface ServicesApiGetTransactionalMessagesQueryParams {
  searchString?: string;
}

export interface ServicesApiTransactionalMessage {
  id: number;
  name: string;
  type?: string;
  description?: string;
  state?: string;
  created?: number;
  updated?: number;
}

export interface ServicesApiGetTransactionalMessagesResponse {
  transactional_messages: ServicesApiTransactionalMessage[];
}

/**
 * Delivery Filters API Types
 */
export interface ServicesApiGetDeliveryFiltersPathParams {
  environment_id: number;
}

export interface ServicesApiFilterMessage {
  action_id: string;
  campaign_id: string;
  newsletter_id: string;
  output_id: string;
  subject: string;
  type: string; // Channel: 'email', 'twilio', 'webhook', 'in_app', 'push', 'slack'
  can_segment_on_variation?: boolean;
  template_id?: string;
  transactional_message_id?: string;
  in_app_broadcast_id?: string;
  variation?: number | null;
  language?: string | null;
}

export interface ServicesApiGetDeliveryFiltersResponse {
  campaigns?: Array<{ id: string; name: string; type: string; state: string; first_started: number | null }>;
  newsletters?: Array<{ id: string; name: string }>;
  messages?: ServicesApiFilterMessage[];
  segmentable?: ServicesApiFilterMessage[];
  transactional_messages?: Array<{ id: string; name: string; type: string }>;
  branch_messages?: ServicesApiFilterMessage[];
  in_app_broadcasts?: Array<{ id: string; name: string; output_id: string }>;
}

/**
 * Backfills (Tasks) API Types
 */

export type ServicesApiBackfillState = 'Queued' | 'InProgress' | 'Failure' | 'Success' | 'Retrying' | 'Canceled';

export type ServicesApiBackfillPriority =
  | 'Priority'
  | 'Default'
  | 'Background'
  | 'Import'
  | 'SendNewsletter'
  | 'ProfileExport'
  | 'DeliveriesExport'
  | 'SQL_Import'
  | 'RunAlias';

export interface ServicesApiBackfill {
  environment_id: number;
  id: number;
  object_id?: number;
  object_name?: string;
  object_version?: number;
  object_deleted?: boolean;
  template_ids?: string[];
  delivery_ids?: string[];
  priority: ServicesApiBackfillPriority;
  type: string;
  state: ServicesApiBackfillState;
  status: string;
  created: number;
  last_updated: number;
  started?: number;
  completed?: number;
  error?: string;
}

export interface ServicesApiGetBackfillsPathParams {
  environment_id: number;
}
export interface ServicesApiGetBackfillsQueryParams {
  continuation?: string;
  state?: BackfillState;
  type?: BackfillType;
}

export const backfillStates = ['Queued', 'InProgress', 'Failure', 'Success', 'Retrying', 'Canceled'] as const;
export type BackfillState = (typeof backfillStates)[number];

export interface ServicesApiGetBackfillsResponse {
  backfills: ServicesApiBackfill[];
  meta: {
    pagination: {
      page: number;
      size: number;
    };
    continuation: string;
  };
}

/**
 * Deliverability API Types
 */

export interface ServicesApiInboxProviderMetrics {
  attempted: number[];
  bounced: number[];
  clicked: number[];
  converted: number[];
  created: number[];
  deferred: number[];
  delivered: number[];
  drafted: number[];
  failed: number[];
  human_clicked: number[];
  human_opened: number[];
  opened: number[];
  prefetch_opened: number[];
  sent: number[];
  spammed: number[];
  suppressed: number[];
  topic_unsubscribed: number[];
  undeliverable: number[];
  unsubscribed: number[];
}

export interface ServicesApiGetDeliverabilityMetricsResponse {
  inbox_provider_metrics: {
    [provider: string]: ServicesApiInboxProviderMetrics;
  };
}

export interface ServicesApiGetDeliverabilityMetricsPathParams {
  environment_id: number;
}

export interface ServicesApiGetDeliverabilityMetricsQueryParams {
  version: '2';
  start: number; // Unix timestamp
  end: number; // Unix timestamp
  res: 'days';
}

const c = initContract();
export const contract = c.router({
  /**
   * Account API
   */
  getAccount: {
    method: 'GET',
    path: '/accounts/current',
    responses: {
      200: c.type<ServicesApiAccountResponse>(),
    },
    summary: 'Get account data based on account ID',
  },
  getAccountById: {
    method: 'GET',
    path: '/accounts/:accountId',
    pathParams: c.type<ServicesApiGetAccountByIdPathParams>(),
    responses: {
      200: c.type<ServicesApiAccountResponse>(),
    },
    summary: 'Get account data based on account ID',
  },
  getAccountByEnvironmentId: {
    method: 'GET',
    path: '/account_by_environment/:environmentId',
    pathParams: c.type<ServicesApiGetAccountByEnvironmentIdPathParams>(),
    responses: {
      200: c.type<ServicesApiAccountResponse>(),
    },
    summary: 'Get account data based on environment ID',
  },

  /**
   * Notifications API
   */
  getNotifications: {
    method: 'GET',
    path: '/accounts/:accountId/notifications',
    pathParams: c.type<ServicesApiGetNotificationsPathParams>(),
    responses: {
      200: c.type<ServicesApiGetNotificationsResponse>(),
    },
    summary: 'Get all notifications',
  },
  markNotificationsAsViewed: {
    method: 'POST',
    path: '/accounts/:accountId/notifications/mark_as_viewed',
    pathParams: c.type<ServicesApiMarkNotificationsAsViewedPathParams>(),
    body: c.type<ServicesApiMarkNotificationsAsViewedBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Mark notifications as viewed',
  },

  /**
   * Copilot API
   */
  createCopilotSession: {
    method: 'POST',
    path: '/accounts/:accountId/copilot',
    pathParams: c.type<ServicesApiCreateCopilotSessionPathParams>(),
    body: c.type<object>(),
    responses: {
      200: c.type<ServicesApiCreateCopilotSessionResponse>(),
    },
    summary: 'Start a new copilot session',
  },
  deleteCopilotSession: {
    method: 'DELETE',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<ServicesApiDeleteCopilotSessionPathParams>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Stop a copilot session',
  },
  getCopilotSession: {
    method: 'GET',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<ServicesApiGetCopilotSessionPathParams>(),
    responses: {
      200: c.type<ServicesApiGetCopilotSessionResponse>(),
    },
    summary: 'Get messages from a copilot session',
  },
  getCopilotFileUploadSignature: {
    method: 'POST',
    path: '/accounts/:accountId/copilot/:sessionId/sign',
    pathParams: c.type<ServicesApiGetCopilotFileUploadSignaturePathParams>(),
    body: c.type<ServicesApiGetCopilotFileUploadSignatureBody>(),
    responses: {
      200: c.type<ServicesApiGetCopilotFileUploadSignatureResponse>(),
    },
    summary: 'Get signed URL for file upload to GCS',
  },
  getCopilotFileDownloadUrl: {
    method: 'GET',
    path: '/accounts/:accountId/copilot/:sessionId/files/:filename',
    pathParams: c.type<ServicesApiGetCopilotFileDownloadUrlPathParams>(),
    responses: {
      200: c.type<ServicesApiGetCopilotFileDownloadUrlResponse>(),
    },
    summary: 'Get signed URL for file download from GCS',
  },
  updateCopilotSession: {
    method: 'PUT',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<ServicesApiUpdateCopilotSessionPathParams>(),
    body: c.type<ServicesApiUpdateCopilotSessionBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Update copilot session name',
  },
  getCopilotSessions: {
    method: 'GET',
    path: '/accounts/:accountId/copilot',
    pathParams: c.type<ServicesApiGetCopilotSessionsPathParams>(),
    query: c.type<ServicesApiGetCopilotSessionsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetCopilotSessionsResponse>(),
    },
    summary: 'Get all copilot sessions',
  },
  recordCopilotFeedback: {
    method: 'POST',
    path: '/accounts/:accountId/copilot/:sessionId/logs/:logId/feedback',
    pathParams: c.type<ServicesApiRecordCopilotFeedbackPathParams>(),
    body: c.type<ServicesApiRecordCopilotFeedbackBody>(),
    responses: {
      200: c.type<ServicesApiRecordCopilotFeedbackResponse>(),
    },
    summary: 'Record feedback for a copilot response',
  },
  getAIContextOverrides: {
    method: 'GET',
    path: '/environments/:workspaceId/ai_context/overrides',
    pathParams: c.type<ServicesApiGetAIContextOverridesPathParams>(),
    responses: {
      200: c.type<ServicesApiGetAIContextOverridesResponse>(),
    },
    summary: 'Get AI context overrides',
  },
  updateAIContextOverrides: {
    method: 'PUT',
    path: '/environments/:workspaceId/ai_context/overrides',
    pathParams: c.type<ServicesApiUpdateAIContextOverridesPathParams>(),
    body: c.type<ServicesApiUpdateAIContextOverridesBody>(),
    responses: {
      200: c.type<ServicesApiUpdateAIContextOverridesResponse>(),
    },
    summary: 'Update AI context overrides',
  },

  /**
   * User Feedback API
   */
  submitFeedback: {
    method: 'POST',
    path: '/accounts/:accountId/feedback',
    pathParams: c.type<ServicesApiSubmitFeedbackPathParams>(),
    body: c.type<ServicesApiSubmitFeedbackBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Submit user feedback',
  },

  /**
   * OAuth2 MCP API
   */
  oauthMcp: {
    method: 'POST',
    path: '/oauth2/mcp',
    contentType: 'application/x-www-form-urlencoded',
    body: c.type<string>(),
    responses: {
      200: c.type<ServicesApiOAuthMcpResponse>(),
    },
    summary: 'Handle OAuth2 authorization for MCP',
  },

  /**
   * Zendesk Chat API
   */
  getZendeskChatAccessToken: {
    method: 'GET',
    path: '/accounts/:accountId/users/:userId/zendesk_chat',
    pathParams: c.type<ServicesApiGetZendeskChatAccessTokenPathParams>(),
    responses: {
      200: c.type<ServicesApiGetZendeskChatAccessTokenResponse>(),
    },
    summary: 'Get Zendesk chat access token and configuration',
  },
  refreshZendeskChatToken: {
    method: 'POST',
    path: '/accounts/:accountId/users/:userId/zendesk_chat/token',
    pathParams: c.type<ServicesApiRefreshZendeskChatTokenPathParams>(),
    body: c.type<void>(),
    responses: {
      200: c.type<ServicesApiRefreshZendeskChatTokenResponse>(),
    },
    summary: 'Generate new Zendesk chat token',
  },

  /**
   * Tracking API
   */
  trackEvent: {
    method: 'POST',
    path: '/environments/:workspaceId/track_event',
    pathParams: c.type<ServicesApiTrackEventPathParams>(),
    body: c.type<ServicesApiTrackEventBody>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Track an event',
  },

  /**
   * Audit Logs API
   */
  getAllUsers: {
    method: 'GET',
    path: '/accounts/:account_id/all_users',
    pathParams: c.type<ServicesApiGetAllUsersPathParams>(),
    responses: {
      200: c.type<ServicesApiGetAllUsersResponse>(),
    },
    summary: 'Get all users for an account',
  },
  getAccountAuditLogs: {
    method: 'GET',
    path: '/accounts/:account_id/audit_logs',
    pathParams: c.type<ServicesApiGetAccountAuditLogsPathParams>(),
    query: c.type<ServicesApiAuditLogsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAuditLogsResponse>(),
    },
    summary: 'Get audit logs for an account',
  },
  getWorkspaceAuditLogs: {
    method: 'GET',
    path: '/environments/:environment_id/audit_logs',
    pathParams: c.type<ServicesApiGetWorkspaceAuditLogsPathParams>(),
    query: c.type<ServicesApiAuditLogsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAuditLogsResponse>(),
    },
    summary: 'Get audit logs for a workspace',
  },
  getAccountAuditLogById: {
    method: 'GET',
    path: '/accounts/:account_id/audit_log/:id',
    pathParams: c.type<ServicesApiGetAccountAuditLogByIdPathParams>(),
    query: c.type<ServicesApiGetAuditLogByIdQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAuditLogByIdResponse>(),
    },
    summary: 'Get a single audit log by ID for an account',
  },
  getWorkspaceAuditLogById: {
    method: 'GET',
    path: '/environments/:environment_id/audit_log/:id',
    pathParams: c.type<ServicesApiGetWorkspaceAuditLogByIdPathParams>(),
    query: c.type<ServicesApiGetAuditLogByIdQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetAuditLogByIdResponse>(),
    },
    summary: 'Get a single audit log by ID for a workspace',
  },
  exportAccountAuditLogs: {
    method: 'POST',
    path: '/accounts/:account_id/audit_logs/export',
    pathParams: c.type<ServicesApiExportAccountAuditLogsPathParams>(),
    body: c.type<ServicesApiExportAuditLogsBody>(),
    responses: {
      200: c.type<ServicesApiExportAuditLogsResponse>(),
    },
    summary: 'Create an export of account-level audit logs',
  },
  exportWorkspaceAuditLogs: {
    method: 'POST',
    path: '/environments/:environment_id/audit_logs/export',
    pathParams: c.type<ServicesApiExportWorkspaceAuditLogsPathParams>(),
    body: c.type<ServicesApiExportAuditLogsBody>(),
    responses: {
      200: c.type<ServicesApiExportAuditLogsResponse>(),
    },
    summary: 'Create an export of workspace-level audit logs',
  },

  /**
   * Search API
   */
  search: {
    method: 'GET',
    path: '/environments/:workspaceId/search',
    pathParams: c.type<ServicesApiSearchPathParams>(),
    query: c.type<ServicesApiSearchQueryParams>(),
    responses: {
      200: c.type<ServicesApiSearchResponse>(),
    },
    summary: 'Search across workspace resources',
  },

  /**
   * Campaigns API
   */
  getCampaigns: {
    method: 'GET',
    path: '/environments/:environment_id/campaigns',
    pathParams: c.type<ServicesApiGetCampaignsPathParams>(),
    query: c.type<ServicesApiGetCampaignsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetCampaignsResponse>(),
    },
    summary: 'Get all campaigns for a workspace',
  },

  /**
   * Segments API
   */
  getSegments: {
    method: 'GET',
    path: '/environments/:environment_id/segments',
    pathParams: c.type<ServicesApiGetSegmentsPathParams>(),
    responses: {
      200: c.type<ServicesApiGetSegmentsResponse>(),
    },
    summary: 'Get all segments for a workspace',
  },

  /**
   * Get a single segment by ID (for segment conditions preview)
   */
  getSegmentById: {
    method: 'GET',
    path: '/environments/:environment_id/segments/:segment_id',
    pathParams: c.type<{ environment_id: number; segment_id: number }>(),
    responses: {
      200: c.type<{
        segment: {
          id: number;
          name: string;
          type: string;
          state: string;
          description?: string;
          conditions?: Record<string, unknown>;
          created_at?: number;
          updated_at?: number;
          people_count?: number;
          archived?: boolean;
        };
      }>(),
      404: c.type<{
        error: string;
      }>(),
    },
    summary: 'Get a single segment by ID with full details including conditions',
  },

  /**
   * Newsletters API
   */
  getNewsletters: {
    method: 'GET',
    path: '/environments/:environment_id/newsletters',
    pathParams: c.type<ServicesApiGetNewslettersPathParams>(),
    query: c.type<ServicesApiGetNewslettersQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetNewslettersResponse>(),
    },
    summary: 'Get all newsletters for a workspace',
  },

  /**
   * Transactional Messages API
   */
  getTransactionalMessages: {
    method: 'GET',
    path: '/environments/:environment_id/transactional_messages',
    pathParams: c.type<ServicesApiGetTransactionalMessagesPathParams>(),
    query: c.type<ServicesApiGetTransactionalMessagesQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetTransactionalMessagesResponse>(),
    },
    summary: 'Get all transactional messages for a workspace',
  },

  /**
   * Backfills (Tasks) API
   */
  getBackfills: {
    method: 'GET',
    path: '/environments/:environment_id/backfills',
    pathParams: c.type<ServicesApiGetBackfillsPathParams>(),
    query: c.type<ServicesApiGetBackfillsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetBackfillsResponse>(),
    },
    summary: 'Get backfills (background tasks) for a workspace',
  },

  /**
   * Deliverability API
   */
  getDeliverabilityMetrics: {
    method: 'GET',
    path: '/environments/:environment_id/metrics/deliverability',
    pathParams: c.type<ServicesApiGetDeliverabilityMetricsPathParams>(),
    query: c.type<ServicesApiGetDeliverabilityMetricsQueryParams>(),
    responses: {
      200: c.type<ServicesApiGetDeliverabilityMetricsResponse>(),
    },
    summary: 'Get deliverability metrics by inbox provider',
  },

  /**
   * Delivery Filters API
   * Returns messages (campaign actions) with output_id for condition display
   */
  getDeliveryFilters: {
    method: 'GET',
    path: '/environments/:environment_id/deliveries/filters',
    pathParams: c.type<ServicesApiGetDeliveryFiltersPathParams>(),
    responses: {
      200: c.type<ServicesApiGetDeliveryFiltersResponse>(),
    },
    summary: 'Get delivery filters including messages with output_id for condition display',
  },
});

export type Contract = typeof contract;

const { api, query } = createApi(contract, {
  baseUrl: '/v1',
});

const augmentedApi = {
  ...api,
  getAccountById: async (...params: Parameters<typeof api.getAccountById>): Promise<ServicesApiAccountResponse> => {
    const accountId = params[0].params.accountId;
    if (accountId != null) {
      const cachedAccount = crossAppCache.getValue(`/accounts/${accountId}`);
      if (cachedAccount != null) {
        return cachedAccount as ServicesApiAccountResponse;
      }
    }

    const response = await api.getAccountById(...params);

    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/accounts/${accountId}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }

    return response;
  },
  getAccountByEnvironmentId: async (
    ...params: Parameters<typeof api.getAccountByEnvironmentId>
  ): Promise<ServicesApiAccountResponse> => {
    const environmentId = params[0].params.environmentId;

    if (environmentId != null) {
      const cachedAccount = crossAppCache.getValue(`/account_by_environment/${environmentId}`);
      if (cachedAccount != null) {
        return cachedAccount as ServicesApiAccountResponse;
      }
    }

    const response = await api.getAccountByEnvironmentId(...params);

    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/account_by_environment/${environmentId}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
      crossAppCache.setValue({
        key: `/accounts/${response.account.id}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }
    return response;
  },
  getAccount: async (...params: Parameters<typeof api.getAccount>): Promise<ServicesApiAccountResponse> => {
    const cachedAccount = crossAppCache.getValue(`/accounts/current`);
    if (cachedAccount != null) {
      return cachedAccount as ServicesApiAccountResponse;
    }

    const response = await api.getAccount(...params);

    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/accounts/current`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });

      crossAppCache.setValue({
        key: `/accounts/${response.account.id}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }

    return response;
  },
};

export { augmentedApi as api, query };
